import { 
    createSlice, 
    createEntityAdapter, 
    PayloadAction,
    EntityState,
    isAnyOf,
    createSelector
} from "@reduxjs/toolkit";
import { CaseObservations } from "../../../api/Observations";
import { createObservationsThunk, getObservationsThunk, updateObservationsThunk } from "./Thunk";
import { RootState } from "../../../app/store";

interface ObservationEntry {
    observation: CaseObservations | null;
    status: 'idle' | 'loading' | 'succeeded' | 'failed';
    error: string | null | undefined;
}

interface ObservationsState {
    [caseId: string]: ObservationEntry;
}

const initialState: ObservationsState = {};

const observationsSlice = createSlice({
    name: 'observations',
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder
            .addCase(updateObservationsThunk.fulfilled, (state, action) => {
                const caseId = action.meta.arg.caseId;
                if (!caseId || !state[caseId]) return;
                
                state[caseId].status = 'succeeded';
                const { request } = action.meta.arg;
                if (state[caseId].observation) {
                    state[caseId].observation = {
                        ...state[caseId].observation,
                        ...request
                    };
                }
            })
            .addMatcher(
                isAnyOf(createObservationsThunk.fulfilled, getObservationsThunk.fulfilled),
                (state, action: PayloadAction<CaseObservations>) => {
                    const caseId = action.payload.case_id;
                    if (!caseId) return;
                    
                    // Initialize entry if it doesn't exist
                    if (!state[caseId]) {
                        state[caseId] = {
                            observation: null,
                            status: 'idle',
                            error: null,
                        };
                    }
                    
                    state[caseId].status = 'succeeded';
                    state[caseId].observation = action.payload;
                }
            )
            .addMatcher(
                isAnyOf(createObservationsThunk.pending, getObservationsThunk.pending, updateObservationsThunk.pending),
                (state, action) => {
                    let caseId: string | undefined;
                    
                    if ('case_id' in action.meta.arg) {
                        caseId = action.meta.arg.case_id;
                    } else if ('caseId' in action.meta.arg) {
                        caseId = action.meta.arg.caseId;
                    }
                    
                    if (!caseId) return;
                    
                    if (!state[caseId]) {
                        state[caseId] = {
                            observation: null,
                            status: 'idle',
                            error: null,
                        };
                    }
                    
                    state[caseId].status = 'loading';
                    state[caseId].error = null;
                }
            )
            .addMatcher(
                isAnyOf(createObservationsThunk.rejected, getObservationsThunk.rejected, updateObservationsThunk.rejected),
                (state, action) => {
                    let caseId: string | undefined;
                    
                    if ('case_id' in action.meta.arg) {
                        caseId = action.meta.arg.case_id;
                    } else if ('caseId' in action.meta.arg) {
                        caseId = action.meta.arg.caseId;
                    }
                    
                    if (!caseId) return;
                    
                    // Initialize entry if it doesn't exist
                    if (!state[caseId]) {
                        state[caseId] = {
                            observation: null,
                            status: 'idle',
                            error: null,
                        };
                    }
                    
                    state[caseId].status = 'failed';
                    state[caseId].error = action.payload ?? action.error.message ?? 'Unknown fetch error';
                }
            );
    },
});

export const observationsReducer = observationsSlice.reducer;

const selectObservationEntry = (
    state: RootState,
    caseId: string | undefined
) => {
    return caseId ? state.observationsData[caseId] : undefined;
};

export const selectCurrentObservation = createSelector(
    [
        (state: RootState) => state,
        (state: RootState, caseId?: string) => caseId
    ],
    (state, caseId) => {
        const entry = selectObservationEntry(state, caseId);
        return entry?.observation ?? null;
    }
);

export const selectObservationsStatus = createSelector(
    [
        (state: RootState) => state,
        (state: RootState, caseId?: string) => caseId
    ],
    (state, caseId) => {
        const entry = selectObservationEntry(state, caseId);
        return entry?.status ?? 'idle';
    }
);

export const selectObservationsError = createSelector(
    [
        (state: RootState) => state,
        (state: RootState, caseId?: string) => caseId
    ],
    (state, caseId) => {
        const entry = selectObservationEntry(state, caseId);
        return entry?.error ?? null;
    }
);